import { useState, useEffect, useCallback } from 'react';
// 2026-09-24 (Jaime ruling): create-your-unit nudge for home-less users.
import { track } from '../services/analyticsTracker';
import { useActiveHomeUnitId } from '../hooks/useActiveHomeUnitId';
import { Plus, Trash2, Edit2, X, Loader2, AlertCircle, Syringe, PawPrint, RefreshCw } from 'lucide-react';
import { UnitPrompt } from '../components/HomeStateBanners';
import { homeApi } from '../services/homeApi';
import { pageErrorFromApi } from '../services/planLimitModal';

interface Pet {
  id: string;
  name: string;
  type: string;
  breed: string;
  birthDate: string;
  vetInfo: string;
  medicalNotes: string;
  vaccinations: string;
}

const petTypeIcons: Record<string, string> = {
  dog: '🐕',
  cat: '🐈',
  bird: '🐦',
  fish: '🐟',
  rabbit: '🐇',
  other: '🐾',
};

const petTypes = [
  { value: 'dog', label: 'Perro' },
  { value: 'cat', label: 'Gato' },
  { value: 'bird', label: 'Ave' },
  { value: 'fish', label: 'Pez' },
  { value: 'rabbit', label: 'Conejo' },
  { value: 'other', label: 'Otro' },
];

const emptyForm = {
  name: '',
  type: 'dog',
  breed: '',
  birthDate: '',
  vetInfo: '',
  medicalNotes: '',
  vaccinations: '',
};

export default function HomePets() {
  // FIX (2026-08-31, Jaime bug): explicit active-unit scoping + refetch on unit change.
  const activeUnitId = useActiveHomeUnitId();
  const [pets, setPets] = useState<Pet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const fetchPets = useCallback(async () => {
    // Jaime 2026-09-30: never fire a unitId-dependent request without a
    // resolved unit — show the unit-picker prompt state instead.
    if (!activeUnitId) { setPets([]); setError(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await homeApi.pets.list(activeUnitId);
      const payload = res.data?.data ?? res.data;
      const pets = payload?.pets || payload?.data?.pets || (Array.isArray(payload) ? payload : []);
      setPets(Array.isArray(pets) ? pets : []);
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al cargar mascotas'));
      setPets([]);
    } finally {
      setLoading(false);
    }
  }, [activeUnitId]);

  useEffect(() => { fetchPets(); }, [fetchPets]);

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    try {
      if (editingId) {
        await homeApi.pets.update(editingId, form, activeUnitId);
      } else {
        await homeApi.pets.create(form, activeUnitId);
        track('pets_pet_created');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchPets();
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al guardar mascota'));
    }
  };

  const handleEdit = (pet: Pet) => {
    setEditingId(pet.id);
    setForm({
      name: pet.name,
      type: pet.type || 'other',
      breed: pet.breed || '',
      birthDate: pet.birthDate || '',
      vetInfo: pet.vetInfo || '',
      medicalNotes: pet.medicalNotes || '',
      vaccinations: pet.vaccinations || '',
    });
    setShowForm(true);
  };

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    try {
      // FIX (2026-09-28, ticket #216): same missing-unitId pattern as shopping delete.
      await homeApi.pets.delete(id, activeUnitId);
      fetchPets();
    } catch (err: any) {
      setError(pageErrorFromApi(err, 'Error al eliminar mascota'));
    }
  };

  const getAge = (birthDate: string) => {
    if (!birthDate) return '';
    const years = new Date().getFullYear() - new Date(birthDate).getFullYear();
    return years > 0 ? `${years} año${years > 1 ? 's' : ''}` : 'Cachorro';
  };

  return (
    <div className="space-y-6">
      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-500/50 text-red-700 dark:text-red-300 p-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => { setError(null); void fetchPets(); }} className="ml-auto flex items-center gap-1 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"><RefreshCw className="w-4 h-4" />Reintentar</button>
          <button onClick={() => setError(null)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"><X className="w-4 h-4" /></button>
        </div>
      )}

      {!activeUnitId && !loading && <UnitPrompt />}

      {/* Add Button */}
      <button
        onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
      >
        <Plus className="w-5 h-5" />
        Agregar Mascota
      </button>

      {/* Form */}
      {showForm && (
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-[var(--v-text-primary)] dark:text-white font-semibold">{editingId ? 'Editar Mascota' : 'Nueva Mascota'}</h3>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Nombre"
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            />
            <select
              value={form.type}
              onChange={e => setForm({ ...form, type: e.target.value })}
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            >
              {petTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input
              type="text"
              value={form.breed}
              onChange={e => setForm({ ...form, breed: e.target.value })}
              placeholder="Raza"
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            />
            <input
              type="date"
              value={form.birthDate}
              onChange={e => setForm({ ...form, birthDate: e.target.value })}
              className="bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <input
            type="text"
            value={form.vetInfo}
            onChange={e => setForm({ ...form, vetInfo: e.target.value })}
            placeholder="Info del veterinario (nombre, teléfono)"
            className="w-full bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
          />
          <textarea
            value={form.medicalNotes}
            onChange={e => setForm({ ...form, medicalNotes: e.target.value })}
            placeholder="Notas médicas"
            rows={2}
            className="w-full bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
          />
          <textarea
            value={form.vaccinations}
            onChange={e => setForm({ ...form, vaccinations: e.target.value })}
            placeholder="Vacunas (separadas por coma)"
            rows={2}
            className="w-full bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none"
          />
          <button
            onClick={handleSubmit}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium"
          >
            {editingId ? 'Guardar Cambios' : 'Agregar Mascota'}
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500 dark:text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Cargando mascotas...
        </div>
      )}

      {/* Pet Cards Grid */}
      {!loading && !error && activeUnitId && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pets.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-400 dark:text-slate-500">
              <PawPrint className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
              <p>No hay mascotas registradas</p>
              <p className="text-sm mt-1">¡Agrega tu primera mascota!</p>
              <button
                type="button"
                onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }}
                className="mt-4 rounded-xl bg-[#36CCD8] px-4 py-2.5 text-sm font-semibold text-white"
              >
                Agregar mi primera mascota
              </button>
            </div>
          ) : (
            pets.map((pet) => (
              <div key={pet.id} className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)] group">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-4xl flex-shrink-0">
                    {petTypeIcons[pet.type] || '🐾'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold text-[var(--v-text-primary)] dark:text-white">{pet.name}</h3>
                      <div className="flex gap-1">
                        <button onClick={() => handleEdit(pet)} className="text-gray-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 p-1">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setPendingDeleteId(pet.id)} className="text-gray-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-600 dark:text-red-400 p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-gray-500 dark:text-slate-400 text-sm">{pet.breed || 'Sin raza'}</p>
                    <div className="flex gap-3 mt-1 text-sm text-gray-400 dark:text-slate-500">
                      {pet.birthDate && <span>🎂 {getAge(pet.birthDate)}</span>}
                    </div>
                  </div>
                </div>
                {(pet.vetInfo || pet.medicalNotes || pet.vaccinations) && (
                  <div className="mt-4 space-y-2 border-t border-[var(--v-surface-3)] pt-3">
                    {pet.vetInfo && (
                      <p className="text-gray-700 dark:text-slate-300 text-sm">🏥 {pet.vetInfo}</p>
                    )}
                    {pet.medicalNotes && (
                      <p className="text-gray-500 dark:text-slate-400 text-sm">📋 {pet.medicalNotes}</p>
                    )}
                    {pet.vaccinations && (
                      <p className="text-gray-500 dark:text-slate-400 text-sm flex items-center gap-1">
                        <Syringe className="w-3 h-3" /> {pet.vaccinations}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* (2026-09-24, wave-3) in-app confirm — no native confirm() on delete (same pattern as F-10) */}
      {pendingDeleteId && (
        <ConfirmDialog
          title="Eliminar mascota"
          message="¿Eliminar esta mascota? Esta acción no se puede deshacer."
          confirmLabel="Eliminar"
          confirmColor="red"
          onConfirm={() => { const id = pendingDeleteId; setPendingDeleteId(null); if (id) handleDelete(id); }}
          onCancel={() => setPendingDeleteId(null)}
        />
      )}
    </div>
  );
}

// ─── Confirm Dialog (in-app, mirrors Projects.tsx F35) ──────────────────────
function ConfirmDialog({ title, message, onConfirm, onCancel, confirmLabel, confirmColor }: {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel: string;
  confirmColor: 'green' | 'red';
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
     
      <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-xl border border-slate-200 dark:border-[var(--v-surface-3)] p-6 max-w-sm w-full">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{title}</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onConfirm}
            className={`flex-1 py-2.5 rounded-lg font-medium text-white transition-colors ${
              confirmColor === 'green' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {confirmLabel}
          </button>
          <button
            onClick={onCancel}
            className="flex-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 py-2.5 rounded-lg font-medium transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
